/**
 * usePageZoom — per-page zoom with per-browser persistence.
 *
 * The workshop SM asked for a way to zoom in/out the Invoice page WITHOUT
 * affecting the rest of the app (browser Ctrl+/- zooms everything). This
 * hook exposes a `scale` between 0.5 and 2.0 that's persisted in
 * localStorage under a page-scoped key so the setting sticks per-user
 * per-page across reloads.
 *
 * The consumer wraps the page content in a `<div style={{ transform:
 * `scale(${scale})`, transformOrigin: 'top left', width: `${100/scale}%` }}>`.
 * The `width` compensation keeps the horizontal viewport correct so
 * zooming out doesn't leave a blank strip on the right.
 */
import { useState, useEffect, useCallback } from 'react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.0;
const STEP = 0.1;
const DEFAULT_SCALE = 1.0;
const STORAGE_PREFIX = 'pageZoom.';

function clamp(value) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(value.toFixed(2))));
}

export function usePageZoom(pageKey) {
  const storageKey = `${STORAGE_PREFIX}${pageKey}`;
  const [scale, setScaleState] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = parseFloat(cached);
        if (!Number.isNaN(parsed)) return clamp(parsed);
      }
    } catch { /* localStorage unavailable — fine, fall through */ }
    return DEFAULT_SCALE;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(scale));
    } catch { /* noop */ }
  }, [storageKey, scale]);

  const setScale = useCallback((next) => {
    setScaleState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      return clamp(value);
    });
  }, []);

  const zoomIn = useCallback(() => setScale((s) => s + STEP), [setScale]);
  const zoomOut = useCallback(() => setScale((s) => s - STEP), [setScale]);
  const reset = useCallback(() => setScale(DEFAULT_SCALE), [setScale]);

  return { scale, setScale, zoomIn, zoomOut, reset, MIN_SCALE, MAX_SCALE };
}
